import { Lightning } from '@lightningjs/sdk';

/**
 * A fixed width strip of bars that flows left to right: the newest sample is always the rightmost bar and older
 * ones move left until they fall off the edge, so "now" is where the eye already expects it.
 *
 * Redrawing every bar on each sample is deliberate. It is O(samples) rather than the O(1) of a wrapping ring, but
 * 32 bars a few times a second is far less work than a single row of tiles does on one keypress, and it avoids the
 * write head marker that a wrapping strip needs in order to be readable at all.
 *
 * With no `max` the strip scales to the tallest value currently visible, so the scale follows the window.
 */
export default class Sparkline extends Lightning.Component {
  _construct() {
    this._samples = 0;
    this._barHeight = 0;
    this._max = 1;
    this._autoScale = false;
    this._values = [];
  }

  /**
   * @param {Object} value `{ samples, barWidth, h, max, color }`, `max` omitted to scale to the visible window
   */
  set config({ samples, barWidth, h, max, color }) {
    this._samples = samples;
    this._barHeight = h;
    this._autoScale = !max;
    this._max = max || 1;
    this._values = new Array(samples).fill(0);

    const children = [];
    for (let i = 0; i < samples; i += 1) {
      children.push({
        rect: true,
        // A pixel of gap keeps the bars legible, and every bar shares the default shader so the whole strip
        // still batches into one draw call
        w: Math.max(barWidth - 1, 1),
        h: 0,
        x: i * barWidth,
        y: h,
        mountY: 1,
        color,
      });
    }

    this.patch({ w: samples * barWidth, h, children });
  }

  push(value) {
    if (!this._samples) {
      return;
    }

    this._values.push(Number.isFinite(value) ? Math.max(value, 0) : 0);
    this._values.shift();

    if (this._autoScale) {
      this._max = Math.max(this._getWindowMax(), 1);
    }

    this._draw();
  }

  _getWindowMax() {
    let max = 0;

    for (let i = 0; i < this._samples; i += 1) {
      if (this._values[i] > max) {
        max = this._values[i];
      }
    }

    return max;
  }

  _draw() {
    const bars = this.children;

    for (let i = 0; i < this._samples; i += 1) {
      const value = this._values[i];

      // Anything above zero keeps a pixel, so a very small reading stays visible rather than disappearing
      bars[i].h = value > 0 ? Math.max(Math.round((value / this._max) * this._barHeight), 1) : 0;
    }
  }
}
